/**
 * PlanNodeBrief — the shared "what/inputs/output" presentation of a plan node.
 *
 * One layout, two mounts (owner requirement: the workspace brief must look
 * exactly as the node is presented in the plan workspace):
 *   - PlanNodeDetailPanel (bottom drawer in the plan view) renders
 *     <PlanNodeBriefBody/> under its own resizable-drawer header.
 *   - WorkspaceBrief (top-docked collapsible panel inside the workspace the
 *     node spawned) renders the same body under a collapse header.
 *
 * Pure presentational — DESIGN.md tokens only. PHASE_ACCENT / STATUS_COLOR
 * come from PlanGraphView so every surface shares one colour vocabulary.
 */

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowRight, ChevronDown, ChevronRight, X } from 'lucide-react';
import type { PlanNodeData, PlanEdgeData } from './PlanGraphView';
import { PHASE_ACCENT, STATUS_COLOR } from './PlanGraphView';
import { decodeEntities } from '../../utils/planGraphData';
import { formatCount } from '../../utils/plural';
import MarkdownRenderer from '../ui/MarkdownRenderer';
import type {
  RegulatoryAxis,
  SealedRegulatoryScope,
} from '../../types/workspaceState';

export const WORKSPACE_TYPE_LABELS: Record<string, string> = {
  institution_context: 'Context',
  research: 'Research',
  production: 'Build',
  documentation: 'Document',
  output_package: 'Package',
};

export const STATUS_LABELS: Record<string, string> = {
  planned: 'Planned',
  active: 'In progress',
  done: 'Done',
  blocked: 'Blocked',
  revised: 'Revised',
  needs_review: 'Needs review',
};

const SHAPE_LABELS: Record<string, string> = {
  model_spec: 'Model spec',
  field_mapping: 'Field mapping',
};

type FloatingPlacement = 'above' | 'below';

const FLOATING_GAP = 8;
const FLOATING_VIEWPORT_GUTTER = 8;

function floatingLeft(anchorRect: DOMRect): number {
  return Math.max(FLOATING_VIEWPORT_GUTTER, anchorRect.left);
}

function floatingTop(anchorRect: DOMRect, overlayHeight: number, placement: FloatingPlacement): number {
  if (placement === 'above') {
    return Math.max(FLOATING_VIEWPORT_GUTTER, anchorRect.top - overlayHeight - FLOATING_GAP);
  }
  return anchorRect.bottom + FLOATING_GAP;
}

function cleanString(value: unknown): string | null {
  // Backend stores text HTML-escaped; decode at this display boundary so
  // "&amp;" / "&lt;" never render literally (same convention as the plan graph).
  return typeof value === 'string' && value.trim() ? decodeEntities(value.trim()) : null;
}

function humanizeId(value: unknown): string {
  const text = String(value || '').trim();
  if (!text) return '';
  return text
    // Normalize ':' too (e.g. "node:assumption") so a raw colon / id never
    // leaks into the UI — parity with the backend humanize_artifact_id, which
    // splits on ':'. This is the last-line-of-defense for the no-raw-ids rail.
    .replace(/[_:-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\w\S*/g, (part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase());
}

function getProject(node: PlanNodeData): Record<string, any> | null {
  return node.project && typeof node.project === 'object' ? node.project : null;
}

function getProjectStandard(node: PlanNodeData): Record<string, any> | null {
  const project = getProject(node);
  const standard = project?.project_standard;
  return standard && typeof standard === 'object' ? standard : null;
}

interface DoGItem { text: string; role: string | null }

function dogText(item: unknown): string {
  if (typeof item === 'string') return decodeEntities(item.trim());
  if (item && typeof item === 'object') {
    const obj = item as Record<string, unknown>;
    return (
      cleanString(obj.acceptance_criterion) ||
      cleanString(obj.criterion) ||
      cleanString(obj.description) ||
      cleanString(obj.text) ||
      ''
    );
  }
  return '';
}

// Definition-of-Good criteria, each paired POSITIONALLY (the backend contract:
// definition_of_good[i] is the acceptance criterion for required_artifacts[i])
// with its artifact to recover applies_to_role. A criterion belongs to "this
// node" when its artifact applies to the node's role, or to all roles (untagged
// / multi-role → role stays null). When the two lists don't line up 1:1 we can't
// scope safely, so every criterion is treated as all-roles (flat fallback).
function definitionOfGoodItems(node: PlanNodeData): DoGItem[] {
  const project = getProject(node);
  const rawDef = project?.definition_of_good;
  const defs = Array.isArray(rawDef) ? rawDef : [];
  const reqRaw = project?.required_artifacts;
  const reqs = Array.isArray(reqRaw) ? reqRaw : [];
  const paired = reqs.length === defs.length && defs.length > 0;
  const items: DoGItem[] = [];
  defs.forEach((entry, i) => {
    const text = dogText(entry);
    if (!text) return;
    let role: string | null = null;
    if (paired) {
      const art = reqs[i] as Record<string, any> | undefined;
      const applies = art?.applies_to_role;
      if (typeof applies === 'string') role = applies;
      else if (Array.isArray(applies) && applies.length === 1) role = applies[0];
      // null / multi-role → applies to all nodes (role stays null)
    }
    items.push({ text, role });
  });
  return items;
}

function roleLabel(role: string | null): string {
  if (!role) return 'All nodes';
  return role
    .split('_')
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');
}

function governanceLabel(status?: string | null): string | null {
  switch ((status || '').toLowerCase()) {
    case 'governed': return 'Governed';
    case 'ungoverned': return 'Ungoverned';
    case 'waived': return 'Waived';
    default: return null;
  }
}

function verdictLabel(node: PlanNodeData): string | null {
  const raw =
    cleanString(node.validation_verdict) ||
    cleanString(node.overall_verdict) ||
    cleanString(node.deliverable_verdict) ||
    cleanString(node.workspace_validation_status) ||
    cleanString(node.validation_status);
  return raw ? humanizeId(raw) : null;
}

function findStats(node: PlanNodeData): Record<string, any> | null {
  const candidates = [
    node.validator_stats,
    node.validation_stats,
    node.metadata?.validator_stats,
    node.metadata?.validation_stats,
    node.metadata?.last_validation_stats,
    node.metadata?.last_validation && typeof node.metadata.last_validation === 'object'
      ? (node.metadata.last_validation as Record<string, any>).stats
      : null,
    node.metadata?.validation_result && typeof node.metadata.validation_result === 'object'
      ? (node.metadata.validation_result as Record<string, any>).stats
      : null,
  ];
  return candidates.find((item): item is Record<string, any> => !!item && typeof item === 'object') || null;
}

function validationStatsLabel(node: PlanNodeData): string | null {
  const stats = findStats(node);
  if (!stats) return null;
  const declaredTargets = Number(stats.declared_target_fields);
  const mappedTargets = Number(stats.mapped_target_fields);
  if (Number.isFinite(declaredTargets) && declaredTargets > 0 && Number.isFinite(mappedTargets)) {
    return `${mappedTargets}/${declaredTargets} targets mapped`;
  }
  const reviewedNodes = Number(stats.reviewed_nodes ?? stats.validated_this_session);
  const totalNodes = Number(stats.total_nodes);
  if (Number.isFinite(reviewedNodes) && Number.isFinite(totalNodes) && totalNodes > 0) {
    return `${reviewedNodes}/${totalNodes} nodes reviewed`;
  }
  const coverageItems = Number(stats.coverage_items);
  if (Number.isFinite(coverageItems) && coverageItems > 0) {
    return formatCount(coverageItems, { one: 'coverage item', other: 'coverage items' });
  }
  const requirementCount = Number(stats.requirement_count);
  if (Number.isFinite(requirementCount) && requirementCount > 0) {
    return `${formatCount(requirementCount, { one: 'requirement', other: 'requirements' })} checked`;
  }
  return null;
}

function resolveShapeId(node: PlanNodeData): string | null {
  const project = getProject(node);
  const candidates = [
    node.deliverable_shape_id,
    node.deliverable_shape,
    node.metadata?.deliverable_shape_id,
    node.metadata?.deliverable_shape,
    node.deliverable,
    project?.deliverable_shape_id,
    project?.deliverable_shape,
    project?.kind,
    project?.preset_id,
  ]
    .map((value) => cleanString(value)?.toLowerCase())
    .filter((value): value is string => !!value);

  for (const value of candidates) {
    const normalized = value.replace(/[-\s]+/g, '_');
    if (normalized.includes('field_mapping') || normalized.includes('data_mapping') || normalized.includes('mapping')) {
      return 'field_mapping';
    }
    if (normalized.includes('model_spec') || normalized.includes('model_risk') || normalized.includes('model_requirements')) {
      return 'model_spec';
    }
  }
  return null;
}

export function resolveShapeLabel(node: PlanNodeData): string | null {
  const shapeId = resolveShapeId(node);
  return shapeId ? SHAPE_LABELS[shapeId] || humanizeId(shapeId) : null;
}

export function ShapeBadge({
  node,
  prefix,
}: {
  node: PlanNodeData;
  prefix?: string;
}) {
  const label = resolveShapeLabel(node);
  if (!label) return null;
  return (
    <span className="inline-flex items-center gap-1.5 px-2 h-5 rounded bg-accent-tint border border-accent-border text-xs font-medium text-accent-hover flex-shrink-0">
      {prefix && <span className="text-fg-tertiary">{prefix}</span>}
      <span>&lt;&lt; {label} &gt;&gt;</span>
    </span>
  );
}

function StandardDefinitionPopover({
  standard,
  node,
}: {
  standard: Record<string, any>;
  node: PlanNodeData;
}) {
  const [open, setOpen] = useState(false);
  const [restOpen, setRestOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const label = [
    cleanString(standard.standard_id),
    cleanString(standard.version),
  ].filter(Boolean).join(' · ');

  const items = definitionOfGoodItems(node);
  const nodeRole = cleanString(node.deliverable_role);
  // "Applies to this node" = criteria whose artifact targets this node's role or
  // every role; the remainder is the rest of the project standard (collapsed).
  const appliesHere = items.filter((it) => it.role === null || it.role === nodeRole);
  const rest = items.filter((it) => it.role !== null && it.role !== nodeRole);
  const restGroups = new Map<string, string[]>();
  rest.forEach((it) => {
    const key = roleLabel(it.role);
    const arr = restGroups.get(key) || [];
    arr.push(it.text);
    restGroups.set(key, arr);
  });
  const hasSplit = rest.length > 0;
  const nodeContext = [cleanString(node.text), nodeRole ? roleLabel(nodeRole) : null]
    .filter(Boolean)
    .join(' · ');

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!label) return null;
  if (items.length === 0) {
    return (
      <span className="inline-flex items-center px-2 h-6 rounded-md bg-surface border border-border-default text-xs font-medium text-fg-secondary">
        {label}
      </span>
    );
  }

  return (
    <div className="inline-flex">
      <button
        ref={triggerRef}
        type="button"
        onClick={(event) => {
          if (open) {
            setOpen(false);
            return;
          }
          setAnchorRect(event.currentTarget.getBoundingClientRect());
          setOpen(true);
        }}
        aria-expanded={open}
        className="inline-flex items-center px-2 h-6 rounded-md bg-surface border border-border-default text-xs font-medium text-fg-secondary hover:bg-primary-tint hover:border-primary-border transition-colors"
      >
        {label}
      </button>
      {open && anchorRect && createPortal(
        <div
          ref={popoverRef}
          role="dialog"
          aria-label="Definition of good"
          className="fixed z-[1000] w-80 max-h-[70vh] overflow-y-auto rounded-lg bg-surface border border-border-default shadow-bark-md p-3 text-left"
          style={{
            left: floatingLeft(anchorRect),
            top: anchorRect.bottom + FLOATING_GAP,
          }}
        >
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-fg-tertiary">
                Definition of good
              </div>
              <div className="text-xs font-semibold text-fg">{label}</div>
              {nodeContext && (
                <div className="text-[11px] text-fg-tertiary truncate">Held to: {nodeContext}</div>
              )}
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="p-1 rounded text-fg-tertiary hover:text-fg-secondary hover:bg-bg-warm flex-shrink-0"
              aria-label="Close standard definition"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="border-t border-border-default pt-2">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-fg-tertiary">
                {hasSplit ? 'Applies to this node' : 'Criteria'}
              </span>
              <span className="text-[11px] font-medium text-fg-tertiary">{appliesHere.length}</span>
            </div>
            {appliesHere.length === 0 ? (
              <p className="text-xs text-fg-tertiary italic">No node-specific criteria.</p>
            ) : (
              <ul className="space-y-1.5">
                {appliesHere.map((it, index) => (
                  <li key={`a-${index}`} className="text-xs leading-relaxed text-fg-secondary">
                    {it.text}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {hasSplit && (
            <div className="border-t border-border-default mt-2 pt-2">
              <button
                type="button"
                onClick={() => setRestOpen((v) => !v)}
                aria-expanded={restOpen}
                className="w-full flex items-center justify-between gap-2 text-left"
              >
                <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.08em] text-fg-tertiary">
                  {restOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                  Rest of project standard
                </span>
                <span className="text-[11px] font-medium text-fg-tertiary">{rest.length}</span>
              </button>
              {restOpen && (
                <div className="mt-2 space-y-2">
                  {Array.from(restGroups.entries()).map(([group, texts]) => (
                    <div key={group}>
                      <div className="text-[11px] font-semibold text-fg-tertiary mb-1">{group}</div>
                      <ul className="space-y-1.5">
                        {texts.map((t, i) => (
                          <li key={`${group}-${i}`} className="text-xs leading-relaxed text-fg-secondary">
                            {t}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}

function HeldToBlock({ node }: { node: PlanNodeData }) {
  const standard = getProjectStandard(node);
  const governance = governanceLabel(node.completion_governance_status);
  const verdict = verdictLabel(node);
  const stats = validationStatsLabel(node);
  if (!standard && !governance && !verdict) return null;

  return (
    <div className="mt-3 rounded-md border border-border-default bg-bg-warm px-2.5 py-2">
      <div className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.08em] text-fg-tertiary">
        Held to
      </div>
      <div className="space-y-1.5">
        {standard && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] font-medium text-fg-tertiary">Standard</span>
            <StandardDefinitionPopover standard={standard} node={node} />
          </div>
        )}
        {governance && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] font-medium text-fg-tertiary">Governance</span>
            <span
              className={`inline-flex items-center px-2 h-5 rounded text-[11px] font-medium ${
                node.completion_governance_status === 'governed' ? 'bg-success-bg text-success' :
                node.completion_governance_status === 'waived' ? 'bg-warning-bg text-warning' :
                node.completion_governance_status === 'ungoverned' ? 'bg-danger-bg text-danger' :
                'bg-surface text-fg-secondary'
              }`}
            >
              {governance}
            </span>
          </div>
        )}
        {verdict && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] font-medium text-fg-tertiary">Validation</span>
            <span className="inline-flex items-center px-2 h-5 rounded bg-surface border border-border-default text-[11px] font-medium text-fg-secondary">
              {verdict}{stats ? ` · ${stats}` : ''}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h4 className="text-[10px] font-bold uppercase tracking-[0.08em] text-fg-tertiary mb-1.5 flex-shrink-0">
      {children}
    </h4>
  );
}

// ---------------------------------------------------------------------------
// Scope tab (BAR-505 slice 5.5, redesigned) — the regulatory scope a
// research node runs under. Compact by design: one line per exposure, basis
// expressed as TODAY / RESEARCHING grouping (never a per-row badge that can
// contradict the label), notes folded behind one expander. Nothing is
// dropped — bounded by structure, full detail one click away.
// ---------------------------------------------------------------------------

function resolvedAxis(ax?: RegulatoryAxis | null): string | null {
  if (!ax || ax.status !== 'resolved') return null;
  const v = (ax.value || '').trim();
  return v || null;
}

function approachLabel(ax?: RegulatoryAxis | null): string {
  if (!ax) return 'unresolved';
  if (ax.status === 'resolved') return (ax.value || '').trim() || 'unresolved';
  if (ax.status === 'not_applicable') return 'n/a';
  return 'unresolved';
}

const REGULATORY_SCOPE_INPUT_SCHEMA = 'research_regulatory_scope_input/v1';
const LOWERCASE_SHA256 = /^[0-9a-f]{64}$/;
const REGULATORY_SCOPE_V1_KEYS = [
  'effective',
  'l1_snapshot',
  'l2_override',
  'provenance',
  'seal_id',
  'seal_note',
  'regulatory_scope_input_schema',
  'regulatory_scope_input_sha256',
] as const;
const REGULATORY_CONTEXT_V1_KEYS = [
  'jurisdiction',
  'level_of_application',
  'approaches',
  'as_of',
  'availability',
] as const;
const REGULATORY_AXIS_V1_KEYS = [
  'status', 'value', 'reason', 'confidence',
] as const;
const SCOPE_APPROACH_V1_KEYS = [
  'scope_descriptor', 'approach', 'basis', 'structure_id', 'scope_note',
] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function hasExactOwnKeys(
  candidate: Record<string, unknown>,
  expected: readonly string[],
): boolean {
  const keys = Object.keys(candidate);
  return keys.length === expected.length && expected.every((key) => (
    Object.prototype.hasOwnProperty.call(candidate, key)
  ));
}

function hasV1AxisShape(value: unknown): boolean {
  if (!isRecord(value) || !hasExactOwnKeys(value, REGULATORY_AXIS_V1_KEYS)) {
    return false;
  }
  return (
    ['resolved', 'not_applicable', 'unresolved'].includes(String(value.status))
    && (value.value === null || typeof value.value === 'string')
    && (value.reason === null || typeof value.reason === 'string')
    && (value.confidence === null || typeof value.confidence === 'string')
  );
}

function hasV1ApproachShape(value: unknown): boolean {
  if (!isRecord(value) || !hasExactOwnKeys(value, SCOPE_APPROACH_V1_KEYS)) {
    return false;
  }
  return (
    typeof value.scope_descriptor === 'string'
    && hasV1AxisShape(value.approach)
    && typeof value.basis === 'string'
    && (value.structure_id === null || typeof value.structure_id === 'string')
    && typeof value.scope_note === 'string'
  );
}

function hasV1ContextShape(value: unknown): boolean {
  if (!isRecord(value) || !hasExactOwnKeys(value, REGULATORY_CONTEXT_V1_KEYS)) {
    return false;
  }
  return (
    hasV1AxisShape(value.jurisdiction)
    && hasV1AxisShape(value.level_of_application)
    && hasV1AxisShape(value.as_of)
    && Array.isArray(value.approaches)
    && value.approaches.every(hasV1ApproachShape)
    && typeof value.availability === 'string'
  );
}

/**
 * Runtime presentation guard for historical API payloads. TypeScript's
 * SealedRegulatoryScope shape cannot protect the UI from legacy or malformed
 * JSON. This intentionally checks only that a recognized content commitment
 * is present; it does not recompute the digest or claim authentication.
 */
export function hasRecognizedRegulatoryScopeCommitment(scope: unknown): boolean {
  if (!isRecord(scope)) return false;
  const candidate = scope;
  return (
    hasExactOwnKeys(candidate, REGULATORY_SCOPE_V1_KEYS)
    && candidate.regulatory_scope_input_schema === REGULATORY_SCOPE_INPUT_SCHEMA
    && typeof candidate.regulatory_scope_input_sha256 === 'string'
    && LOWERCASE_SHA256.test(candidate.regulatory_scope_input_sha256)
    && hasV1ContextShape(candidate.effective)
    && hasV1ContextShape(candidate.l1_snapshot)
    && hasV1ContextShape(candidate.l2_override)
    && (candidate.provenance === 'inherited' || candidate.provenance === 'elicited')
    && (candidate.seal_id === null || typeof candidate.seal_id === 'string')
    && typeof candidate.seal_note === 'string'
  );
}

/** One compact scope row: descriptor (truncated, full text + note on hover)
 *  + approach chip. Basis suffix only for the rare non-target researching
 *  bases (comparative / hypothetical). */
function ScopeRow({ row }: { row: NonNullable<SealedRegulatoryScope['effective']>['approaches'][number] }) {
  // basis is a free string made load-bearing by the grouping — normalize case
  // so an agent-emitted 'Actual' can't land under 'Researching →'.
  const basis = (row.basis || 'actual').trim().toLowerCase();
  const suffix = basis !== 'actual' && basis !== 'target' ? basis : null;
  // Hover recovers BOTH the (possibly truncated) descriptor and the note —
  // nothing is unrecoverable.
  const hover = [row.scope_descriptor, row.scope_note].filter(Boolean).join(' — ');
  return (
    <li
      className="flex items-center gap-2 py-1 min-w-0"
      title={hover || undefined}
    >
      <span className="text-[12px] font-medium text-fg truncate min-w-0">
        {row.scope_descriptor}
      </span>
      {row.structure_id && (
        <span className="inline-flex items-center px-1.5 h-4 rounded-full font-mono text-[10px] font-semibold uppercase tracking-wider bg-bg-warm text-fg-tertiary flex-shrink-0">
          {row.structure_id}
        </span>
      )}
      <span className="ml-auto inline-flex items-center gap-1 flex-shrink-0">
        <span className="inline-flex items-center px-1.5 h-5 rounded bg-surface border border-border-default text-[11px] font-medium text-fg-secondary">
          {approachLabel(row.approach)}
        </span>
        {suffix && (
          <span className="inline-flex items-center px-1.5 h-5 rounded bg-warning-bg text-warning text-[10px] font-medium">
            {suffix}
          </span>
        )}
      </span>
    </li>
  );
}

function ScopeGroup({
  label,
  rows,
}: {
  label: string;
  rows: NonNullable<SealedRegulatoryScope['effective']>['approaches'];
}) {
  if (rows.length === 0) return null;
  return (
    <div className="mb-2 last:mb-0">
      <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-fg-tertiary mb-0.5">
        {label}
      </div>
      <ul className="divide-y divide-divider">
        {rows.map((row, i) => (
          <ScopeRow
            key={`${row.scope_descriptor}-${row.basis}-${row.structure_id || ''}-${i}`}
            row={row}
          />
        ))}
      </ul>
    </div>
  );
}

/** Full Scope tab content. Exported for reuse/tests. */
export function ScopeTabContent({ scope }: { scope?: SealedRegulatoryScope | null }) {
  const [notesOpen, setNotesOpen] = useState(false);

  if (!scope) {
    return (
      <p className="text-[12px] text-fg-tertiary leading-snug">
        No scope pinned — the agent seals one when research needs a scoped
        determination. Quick lookups run unscoped.
      </p>
    );
  }

  const commitmentPresent = hasRecognizedRegulatoryScopeCommitment(scope);
  if (!commitmentPresent) {
    return (
      <div className="max-w-xl">
        <span
          className="inline-flex items-center gap-1 px-1.5 h-4 rounded-full bg-warning-bg border border-border-default text-[10px] font-medium text-warning"
          title="Scope commitment unverified: the stored v1 envelope is missing or malformed"
        >
          <span className="w-1 h-1 rounded-full bg-warning" />
          unverified scope
        </span>
        <p className="mt-2 text-[12px] text-fg-tertiary leading-snug">
          Stored scope details are hidden until the server produces a valid v1 commitment envelope.
        </p>
      </div>
    );
  }

  const effective = scope.effective;
  const axes = [
    resolvedAxis(effective?.jurisdiction),
    resolvedAxis(effective?.level_of_application),
    resolvedAxis(effective?.as_of) ? `as-of ${resolvedAxis(effective?.as_of)}` : null,
  ].filter(Boolean) as string[];
  const approaches = effective?.approaches || [];
  const normBasis = (r: { basis?: string }) => (r.basis || 'actual').trim().toLowerCase();
  const today = approaches.filter((r) => normBasis(r) === 'actual');
  const researching = approaches.filter((r) => normBasis(r) !== 'actual');
  const noted = approaches.filter((r) => (r.scope_note || '').trim());

  return (
    <div className="max-w-xl">
      {/* Axes + commitment provenance */}
      <div className="flex items-center gap-2 mb-2 min-w-0">
        {axes.length > 0 && (
          <p className="text-[12px] font-medium text-fg-secondary truncate min-w-0">
            {axes.join(' · ')}
          </p>
        )}
        <span
          className="ml-auto inline-flex items-center gap-1 px-1.5 h-4 rounded-full bg-bg-warm border border-border-default text-[10px] font-medium text-fg-tertiary flex-shrink-0"
          title="Content commitment field present; digest not verified in this view"
        >
          <span className="w-1 h-1 rounded-full bg-fg-tertiary" />
          {scope.provenance} · commitment present
        </span>
      </div>

      {approaches.length === 0 ? (
        <p className="text-[12px] text-fg-tertiary leading-snug">
          No per-exposure approaches recorded.
        </p>
      ) : (
        <>
          <ScopeGroup label="Today" rows={today} />
          <ScopeGroup label="Researching →" rows={researching} />
        </>
      )}

      {/* Footer: inheritance note + notes expander (full text, nothing dropped) */}
      <div className="mt-2 flex items-center gap-3">
        {scope.provenance === 'inherited' && (
          <span className="text-[11px] text-fg-tertiary">
            Inherited from institution profile
          </span>
        )}
        {noted.length > 0 && (
          <button
            type="button"
            onClick={() => setNotesOpen((v) => !v)}
            aria-expanded={notesOpen}
            className="ml-auto inline-flex items-center gap-1 text-[11px] font-medium text-fg-tertiary hover:text-fg-secondary transition-colors"
          >
            Notes ({noted.length})
            {notesOpen
              ? <ChevronDown className="w-3 h-3" />
              : <ChevronRight className="w-3 h-3" />}
          </button>
        )}
      </div>
      {notesOpen && (
        <ul className="mt-1.5 space-y-1 border-t border-divider pt-1.5">
          {noted.map((row, i) => (
            <li key={`note-${i}`} className="text-[11px] leading-snug">
              <span className="font-medium text-fg-secondary">{row.scope_descriptor}</span>
              <span className="text-fg-tertiary"> — {row.scope_note}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Phase chip — same rendering in the plan drawer and the workspace brief. */
export function PhaseChip({ workspaceType }: { workspaceType?: string | null }) {
  if (!workspaceType) return null;
  const accent = PHASE_ACCENT[workspaceType] || 'var(--fg-tertiary)';
  const label = WORKSPACE_TYPE_LABELS[workspaceType] || workspaceType;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 h-5 rounded-full text-[10px] font-semibold uppercase tracking-[0.06em] text-fg-secondary flex-shrink-0"
      style={{
        background: `color-mix(in srgb, ${accent} 14%, var(--surface))`,
        border: `1px solid color-mix(in srgb, ${accent} 40%, var(--border))`,
      }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: accent }} />
      {label}
    </span>
  );
}

/** Status pill — same rendering in the plan drawer and the workspace brief. */
export function StatusPill({ status }: { status: string }) {
  const statusColor = STATUS_COLOR[status] || 'var(--fg-tertiary)';
  return (
    <span className="inline-flex items-center gap-1.5 px-2 h-5 rounded-full bg-bg-warm border border-border-default text-[11px] font-medium text-fg-secondary flex-shrink-0">
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: statusColor }} />
      {STATUS_LABELS[status] || status}
    </span>
  );
}

/** One incoming/outgoing dependency row: connected node + handoff contract.
 *  Long specs render clamped to two lines with a per-row expander — the full
 *  text is always present (visual fold, not truncation). */
export function EdgeRow({
  title,
  spec,
  dotColor,
  outgoing,
}: {
  title: string;
  spec?: string | null;
  dotColor: string;
  outgoing?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  // Heuristic for OFFERING the toggle only when folding plausibly bites;
  // the clamp itself is pure CSS and harmless on short text.
  const foldable = (spec || '').length > 160;
  return (
    <li className="py-2 first:pt-0">
      <div className="flex items-center gap-1.5 min-w-0">
        {outgoing && <ArrowRight className="w-3 h-3 text-fg-tertiary flex-shrink-0" />}
        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: dotColor }} />
        <span className="text-[12px] font-medium text-fg truncate">{title}</span>
        {foldable && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            title={expanded ? 'Collapse contract' : 'Show full contract'}
            className="ml-auto p-0.5 rounded text-fg-tertiary hover:text-fg-secondary hover:bg-bg-warm flex-shrink-0"
          >
            {expanded
              ? <ChevronDown className="w-3.5 h-3.5" />
              : <ChevronRight className="w-3.5 h-3.5" />}
          </button>
        )}
      </div>
      {spec && (
        <p
          className={`mt-1 pl-3 text-[12px] text-fg-secondary leading-snug ${
            foldable && !expanded ? 'line-clamp-2' : ''
          }`}
        >
          {spec}
        </p>
      )}
    </li>
  );
}

/** Light display-only formatting for the planner's node description.
 *  Two deterministic, no-information-loss transforms:
 *
 *  1. Inline "(a) … (b) … (c) …" ENUMERATIONS become markdown bullets — but
 *     ONLY when the markers are a real enumeration: at least three, starting
 *     at (a), consecutively ascending, and never preceded by "point(s)"/
 *     "letter(s)". Legal citations — "points (a) and (b) of Article 36(1)" —
 *     are exactly two markers and/or point-prefixed, so they stay prose
 *     (review finding: the naive transform garbled CRR citations).
 *  2. Lone newlines are promoted to paragraph breaks — CommonMark collapses
 *     single newlines into one run-on paragraph, which was the unformatted
 *     blob this rendering set out to fix.
 */
export function formatNodeDescription(text: string): string {
  let out = text;

  const markerRe = /(?:^|\s)\(([a-h])\)\s/g;
  const letters: string[] = [];
  let enumeration = true;
  let m: RegExpExecArray | null;
  while ((m = markerRe.exec(out)) !== null) {
    // A citation-style marker ("points (a) and (b)", "letter (c)") disqualifies
    // the whole transform — this text is legal prose, not an enumeration.
    const before = out.slice(Math.max(0, m.index - 24), m.index);
    if (/\b(points?|letters?)\s*(?:\([a-h]\)\s*(?:,|and|or)?\s*)*$/i.test(before)) {
      enumeration = false;
      break;
    }
    letters.push(m[1]);
  }
  const consecutiveFromA = letters.every(
    (l, i) => l.charCodeAt(0) === 'a'.charCodeAt(0) + i,
  );
  if (enumeration && letters.length >= 3 && consecutiveFromA) {
    out = out.replace(/(^|\s)\(([a-h])\)\s/g, '\n- **($2)** ');
  }

  // Promote newline runs to paragraph breaks — CommonMark collapses lone
  // newlines into a run-on paragraph (blank lines between list items are
  // legal markdown — a loose list — so this is safe after the transform).
  out = out.replace(/\n+/g, '\n\n');
  return out;
}

type BriefTabId = 'overview' | 'inputs' | 'scope' | 'output';

/**
 * The node-brief body: an always-on header lives in each mount; this body is
 * a TAB SET — Overview (formatted description + validation + held-to) ·
 * Inputs · Scope (research nodes only, BAR-505) · Output. One facet at a
 * time, full width, instead of four keyhole columns in a short drawer.
 * The parent must be a bounded flex column (this is flex-1 min-h-0).
 */
export function PlanNodeBriefBody({
  node,
  edges,
  nodeById,
  regulatoryScope,
}: {
  node: PlanNodeData;
  edges: PlanEdgeData[];
  nodeById: (nodeId: string) => PlanNodeData | undefined;
  /** BAR-505: the research workspace's persisted scope. Lives on the CHILD
   *  workspace's /state (not the plan state), so each mount supplies it:
   *  WorkspaceBrief reads the store's live dictWorkspace, PlanNodeDetailPanel
   *  fetches the child state. Undefined/null → empty-state copy. */
  regulatoryScope?: SealedRegulatoryScope | null;
}) {
  const incoming = edges.filter((e) => e.to_node_id === node.node_id);
  const outgoing = edges.filter((e) => e.from_node_id === node.node_id);
  // The planner's full description; the short deliverable is the only fallback.
  const description = node.description?.trim() || node.deliverable || node.text;
  // Scope is a research-stream concept (BAR-505): tab shown only there.
  const showScope = node.workspace_type === 'research';
  const scopeCommitment: 'present' | 'unverified' | 'unsealed' = !regulatoryScope
    ? 'unsealed'
    : hasRecognizedRegulatoryScopeCommitment(regulatoryScope) ? 'present' : 'unverified';

  const [tab, setTab] = useState<BriefTabId>('overview');
  // Reset to Overview when the node changes under a persistent mount
  // (WorkspaceBrief survives workspace switches; the plan drawer remounts).
  // SYNCHRONOUS adjust-state-on-prop-change — an effect runs after paint and
  // would flash one frame of the new node's content under the old tab.
  const [prevNodeId, setPrevNodeId] = useState(node.node_id);
  if (prevNodeId !== node.node_id) {
    setPrevNodeId(node.node_id);
    setTab('overview');
  }
  // A non-research node can't sit on the scope tab.
  const activeTab: BriefTabId = tab === 'scope' && !showScope ? 'overview' : tab;

  const tabs: { id: BriefTabId; label: string; dot?: 'present' | 'unverified' | 'unsealed' }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'inputs', label: `Inputs · ${incoming.length}` },
    ...(showScope
      ? [{ id: 'scope' as BriefTabId, label: 'Scope', dot: scopeCommitment }]
      : []),
    { id: 'output', label: `Output · ${outgoing.length}` },
  ];

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {/* Tab bar */}
      <div
        role="tablist"
        aria-label="Node details"
        className="flex items-center gap-1 px-3 border-b border-border-default flex-shrink-0"
      >
        {tabs.map((t) => {
          const active = activeTab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setTab(t.id)}
              className={`relative px-2.5 h-8 text-[11px] font-medium transition-colors ${
                active ? 'text-fg' : 'text-fg-tertiary hover:text-fg-secondary'
              }`}
            >
              <span className="inline-flex items-center gap-1.5">
                {t.label}
                {t.dot && (
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${
                      t.dot === 'present'
                        ? 'bg-fg-tertiary'
                        : t.dot === 'unverified' ? 'bg-warning' : 'bg-border-strong'
                    }`}
                    title={t.dot === 'present'
                      ? 'Scope commitment field present (not verified)'
                      : t.dot === 'unverified' ? 'Scope commitment unverified' : 'No scope pinned'}
                  />
                )}
              </span>
              {active && (
                // bg-fg (not bg-primary): --primary is near-invisible against
                // the dark-mode surface (review-verified 1.14:1) — the fg
                // token adapts in both themes.
                <span className="absolute inset-x-1.5 -bottom-px h-0.5 bg-fg rounded-full" />
              )}
            </button>
          );
        })}
      </div>

      {/* Active tab content — full width, one scroll area */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3">
        {activeTab === 'overview' && (
          <div className="max-w-2xl">
            <MarkdownRenderer
              content={formatNodeDescription(description)}
              math={false}
              className="text-fg-secondary [&_p]:text-[13px] [&_p]:leading-relaxed [&_li]:text-[13px] [&_li]:leading-relaxed [&_ul]:my-1 [&_ul]:pl-4 [&_ul]:list-disc [&_ul]:space-y-0.5 [&_ol]:my-1 [&_ol]:pl-4 [&_ol]:list-decimal [&_ol]:space-y-0.5"
            />
            {node.validation_status && (
              <div className="mt-3 flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-fg-tertiary">
                  Validation
                </span>
                <span
                  className={`inline-flex items-center px-1.5 py-px rounded text-[11px] font-medium ${
                    node.validation_status === 'approved' ? 'bg-success-bg text-success' :
                    node.validation_status === 'rejected' ? 'bg-danger-bg text-danger' :
                    node.validation_status === 'review_needed' ? 'bg-warning-bg text-warning' :
                    'bg-bg-warm text-fg-tertiary'
                  }`}
                >
                  {node.validation_status.replace(/_/g, ' ')}
                </span>
              </div>
            )}
            <HeldToBlock node={node} />
          </div>
        )}

        {activeTab === 'inputs' && (
          <div className="max-w-2xl">
            {incoming.length === 0 ? (
              <p className="text-[12px] text-fg-tertiary leading-snug">
                No upstream dependencies — this node starts from project context.
              </p>
            ) : (
              <ul className="divide-y divide-border-default">
                {incoming.map((e) => {
                  const src = nodeById(e.from_node_id);
                  return (
                    <EdgeRow
                      key={e.edge_id}
                      title={src?.text || e.from_node_id}
                      spec={e.output_spec || e.label || src?.deliverable}
                      dotColor={STATUS_COLOR[src?.status || ''] || 'var(--fg-tertiary)'}
                    />
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {activeTab === 'scope' && showScope && (
          <ScopeTabContent scope={regulatoryScope} />
        )}

        {activeTab === 'output' && (
          <div className="max-w-2xl">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              {node.deliverable && (
                <span className="inline-flex items-center px-2 py-0.5 rounded bg-bg-warm border border-border-default text-[11px] font-medium text-fg-secondary">
                  {node.deliverable}
                </span>
              )}
              {resolveShapeLabel(node) && <ShapeBadge node={node} prefix="Produces:" />}
            </div>
            {outgoing.length === 0 ? (
              <p className="text-[12px] text-fg-tertiary leading-snug">
                Terminal output — nothing downstream consumes this node.
              </p>
            ) : (
              <ul className="divide-y divide-border-default">
                {outgoing.map((e) => {
                  const tgt = nodeById(e.to_node_id);
                  return (
                    <EdgeRow
                      key={e.edge_id}
                      outgoing
                      title={tgt?.text || e.to_node_id}
                      spec={e.output_spec || e.label}
                      dotColor={STATUS_COLOR[tgt?.status || ''] || 'var(--fg-tertiary)'}
                    />
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
